import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { openArtifactChatSeed } from '../../chat-seed/open-artifact-chat-seed';
import { agentChatSeedStrategy } from '../../agent/seed/index';

type Params = {
  agentId: string;
  /** When opening from a folder, passed through for chat placement on first message. */
  folderId?: string;
};

export class OpenAgentChatAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Open Agent';
  description = 'Open an agent in chat';
  inputSchema = z.object({
    agentId: z.string().uuid(),
    folderId: z.string().uuid().optional(),
  });
  group = 'Agent';

  private openAbortController: AbortController | undefined;

  constructor(
    private readonly client: TenantedClient['resource'],
    private readonly navigate: (
      path: string,
      options?: { state?: unknown }
    ) => void,
    private readonly orgSlug: string
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    params: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    this.openAbortController?.abort();
    const abortController = new AbortController();
    this.openAbortController = abortController;

    const result = await openArtifactChatSeed({
      client: this.client,
      navigate: this.navigate,
      orgSlug: this.orgSlug,
      strategy: agentChatSeedStrategy,
      artifactId: params.agentId,
      folderId: params.folderId,
      abortSignal: abortController.signal,
    });
    if (result === null) {
      return ok({ resourceId: params.agentId });
    }
    return ok({ resourceId: result.artifactId });
  }
}
